import { css } from '@emotion/react';
import { ReactNode } from 'react';

import { Colors, Typography } from '@zapier/design-system';
import { mediaQueries } from '@zapier/identity-utils';

type Props = {
  children: ReactNode;
  promo?: ReactNode;
  showPromo?: boolean;
  title: ReactNode;
  titlePosition?: 'center' | 'right';
};

const contentWithPromoStyle = css`
  align-items: center;
  display: grid;
  ${mediaQueries.tablet} {
    gap: 50px;
    grid-template-columns: minmax(0px, 1fr) 360px;
  }
  ${mediaQueries.desktop} {
    grid-template-columns: minmax(0px, 1fr) 430px;
  }
`;

const formStyle = css`
  ${mediaQueries.desktop} {
    border-radius: 4px;
    border: 1px solid ${Colors.gray20};
    padding: 20px;
  }
`;

const formTitleStyle = css`
  ${Typography.subHeader5Medium}
  color: ${Colors.neutral800};
  margin: 10px 0 30px 0;
  text-align: center;
  ${mediaQueries.tablet} {
    ${Typography.pageHeader9}
  }
  ${mediaQueries.desktop} {
    ${Typography.pageHeader9}
  }
`;

const innerStyle = css`
  box-sizing: border-box;
  padding: 20px;
  position: relative;
  ${mediaQueries.tablet} {
    margin: 40px auto;
    padding: 0 30px;
    width: 100%;
  }
  ${mediaQueries.desktop} {
    max-width: 1120px;
    padding: 0px 60px;
  }
`;

const innerNonSplit = css`
  ${mediaQueries.tablet} {
    max-width: 500px;
  }
`;

const rootStyle = css`
  background-color: ${Colors.neutral100};
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  min-height: 100%;
`;

const titleStyle = css`
  ${Typography.paragraph1Bold}
  margin-bottom: 30px;
  text-align: center;
  ${mediaQueries.tablet} {
    ${Typography.sectionHeader3}
  }
`;

export function AuthLayout({
  children,
  promo,
  showPromo = true,
  title,
  titlePosition = 'center',
}: Props) {
  const shouldShowPromo = Boolean(showPromo && promo);

  return (
    <div css={rootStyle}>
      <div css={[innerStyle, !shouldShowPromo && innerNonSplit]}>
        {title && titlePosition === 'center' && (
          <h1 css={titleStyle}>{title}</h1>
        )}
        <div css={[shouldShowPromo && contentWithPromoStyle]}>
          {shouldShowPromo && <div data-testid="auth-promo">{promo}</div>}
          <div>
            {title && titlePosition === 'right' && (
              <h2 css={formTitleStyle}>{title}</h2>
            )}
            <div css={formStyle}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
